import { useMemo, useRef, useState, type ReactNode } from "react";
import { Icons } from "../components/icons";

/**
 * Documentación de Sovra Log enfocada a integradores externos.
 *
 * Estructura:
 *   1. Introducción y conceptos clave
 *   2. Pasos para integrar: API key, servicio, eventos, búsqueda
 *   3. Sección avanzada para el endpoint de diagnóstico
 *
 * Toda la autenticación de los endpoints documentados utiliza `X-Api-Key`.
 */
export default function Docs() {
  const apiBase = useApiBase();
  const [active, setActive] = useState<SectionId>("intro");
  const contentRef = useRef<HTMLDivElement>(null);

  function go(id: SectionId) {
    setActive(id);
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="settings-shell">
      <header className="page-header">
        <div>
          <h1>Documentación</h1>
          <div className="sub">
            Guía para conectar una aplicación a Sovra Log, registrar eventos y
            consultarlos desde la interfaz o vía API.
          </div>
        </div>
      </header>

      <div className="settings-layout">
        <nav className="settings-nav">
          {SECTIONS.map((s) => {
            const I = Icons[s.icon];
            return (
              <button
                key={s.id}
                className={active === s.id ? "active" : ""}
                onClick={() => go(s.id)}
              >
                <I size={14} sw={1.7} />
                <span>{s.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="settings-content" ref={contentRef}>
          {active === "intro"    && <IntroSection apiBase={apiBase} />}
          {active === "concepts" && <ConceptsSection />}
          {active === "orgs"     && <OrgsSection />}
          {active === "key"      && <KeySection />}
          {active === "service"  && <ServiceSection />}
          {active === "emit"     && <EmitSection apiBase={apiBase} />}
          {active === "search"   && <SearchSection apiBase={apiBase} />}
          {active === "diagnose" && <DiagnoseSection apiBase={apiBase} />}
        </div>
      </div>
    </div>
  );
}

// ─── Sections meta ───────────────────────────────────────────────────────────

type SectionId =
  | "intro"
  | "concepts"
  | "orgs"
  | "key"
  | "service"
  | "emit"
  | "search"
  | "diagnose";

const SECTIONS: { id: SectionId; label: string; icon: keyof typeof Icons }[] = [
  { id: "intro",    label: "Introducción",                  icon: "Sparkles" },
  { id: "concepts", label: "Conceptos clave",               icon: "Cube" },
  { id: "orgs",     label: "Organizaciones y acceso",       icon: "Users" },
  { id: "key",      label: "1. Crear una API key",          icon: "Shield" },
  { id: "service",  label: "2. Crear un servicio",          icon: "Cube" },
  { id: "emit",     label: "3. Enviar eventos",             icon: "Activity" },
  { id: "search",   label: "4. Buscar traces",              icon: "Search" },
  { id: "diagnose", label: "Endpoint de diagnóstico",       icon: "Cog" },
];

// ─── Sections ────────────────────────────────────────────────────────────────

function IntroSection({ apiBase }: { apiBase: ApiBaseInfo }) {
  return (
    <>
      <DocCard title="¿Qué es Sovra Log?">
        <p>
          Sovra Log es un sistema de observabilidad centralizado para llamadas a
          APIs externas. Tu aplicación reporta dos eventos por cada llamada —
          uno antes de invocar al sistema externo y otro al recibir la respuesta
          —. Sovra Log los agrupa en un <strong>trace</strong> y los hace
          consultables desde la interfaz web o vía API.
        </p>
        <p>
          La autenticación de los endpoints públicos se realiza mediante el
          header <code className="cfg-code">X-Api-Key</code>, lo que permite
          integraciones server-to-server sin manejo de sesión.
        </p>
      </DocCard>

      <DocCard title="Flujo de integración">
        <ol className="docs-numbered">
          <li>
            <strong>Generar una API key</strong> desde Configuración → API Keys.
          </li>
          <li>
            <strong>Registrar un servicio</strong> por cada sistema externo que
            quieras observar (por ejemplo: <code className="cfg-code">pokeapi</code>,
            <code className="cfg-code"> stripe</code>, <code className="cfg-code">renaper</code>).
          </li>
          <li>
            <strong>Enviar eventos</strong> a <code className="cfg-code">POST /v1/ingest/events</code>{" "}
            firmando con la API key.
          </li>
          <li>
            <strong>Consultar traces</strong> desde la interfaz o desde los
            endpoints de lectura, autenticando con la misma API key.
          </li>
        </ol>
      </DocCard>

      <DocCard title="Base URL">
        <p>
          Configurá la URL base de Sovra Log en una variable de entorno de tu
          aplicación. Todos los endpoints documentados cuelgan de esta URL.
        </p>
        <CodeBlock language="bash">{`SOVRA_URL=${apiBase.publicUrl ?? "http://localhost:4100"}`}</CodeBlock>
      </DocCard>
    </>
  );
}

function ConceptsSection() {
  return (
    <>
      <DocCard title="Glosario">
        <p>
          Los siete términos que aparecen en el resto de la documentación.
          Vale la pena leerlos antes de continuar.
        </p>
        <table className="docs-table">
          <tbody>
            <tr>
              <td><strong>Service</strong></td>
              <td>
                Sistema externo bajo observación (por ejemplo, una API de
                terceros o un microservicio interno). Cada servicio se identifica
                por un <code className="cfg-code">service_id</code> único dentro
                de tu organización. Se administran en la sección <em>Servicios</em>.
              </td>
            </tr>
            <tr>
              <td><strong>Event</strong></td>
              <td>
                Registro puntual de un instante en una llamada. Tu aplicación
                emite dos eventos por interacción: el primero antes de invocar
                al servicio (<code className="cfg-code">action: "request"</code>) y
                el segundo al recibir la respuesta
                (<code className="cfg-code">action: "response"</code> o <code className="cfg-code">"error"</code>).
              </td>
            </tr>
            <tr>
              <td><strong>Trace</strong></td>
              <td>
                Conjunto de eventos que comparten un mismo <code className="cfg-code">correlation_id</code>.
                Representa una interacción completa (una solicitud y su
                resultado) y es la unidad principal de análisis en la interfaz.
              </td>
            </tr>
            <tr>
              <td><strong>correlation_id</strong></td>
              <td>
                Identificador único (UUID v4) que tu aplicación genera al iniciar
                una llamada y reutiliza en los eventos asociados. Es el pegamento
                que vincula request y response en un mismo trace.
              </td>
            </tr>
            <tr>
              <td><strong>Tags</strong></td>
              <td>
                Diccionario de etiquetas de negocio adjuntas a cada evento
                (por ejemplo: <code className="cfg-code">module</code>, <code className="cfg-code">operation</code>,
                <code className="cfg-code"> folio</code>, <code className="cfg-code">customer_id</code>).
                Son indexables y se pueden usar como filtros en la búsqueda.
              </td>
            </tr>
            <tr>
              <td><strong>Org (organización)</strong></td>
              <td>
                Tenant al que pertenece cada trace. Aísla los datos entre
                organizaciones distintas. La API key hereda automáticamente
                el <code className="cfg-code">org_id</code> del usuario que la creó.
              </td>
            </tr>
            <tr>
              <td><strong>Verdict</strong></td>
              <td>
                Clasificación automática del resultado del trace, calculada por
                el servidor a partir de los eventos: <code className="cfg-code">external_fault</code>,{" "}
                <code className="cfg-code">internal_fault</code>, <code className="cfg-code">network</code>,{" "}
                <code className="cfg-code">ok</code> o <code className="cfg-code">unknown</code>.
                Se utiliza en el endpoint de diagnóstico.
              </td>
            </tr>
          </tbody>
        </table>
      </DocCard>

      <DocCard title="Modelo mental">
        <pre className="docs-diagram">{`Tu app
  │
  ├── inicia llamada a Servicio externo
  │   └── genera correlation_id (UUID v4)
  │   └── POST /v1/ingest/events  ◄── action: "request"  (sequence: 1)
  │
  ├── recibe respuesta
  │   └── POST /v1/ingest/events  ◄── action: "response" (sequence: 2)
  │
  └── el par de eventos forma un Trace en Sovra Log`}</pre>
      </DocCard>
    </>
  );
}

function OrgsSection() {
  return (
    <>
      <DocCard title="¿Por qué organizaciones?">
        <p>
          Sovra Log es multi-tenant: cada cliente (gobierno, empresa, equipo)
          es una <strong>organización</strong> aislada. Los servicios, traces,
          incidentes y API keys pertenecen a una org específica y no son
          visibles desde otra. La separación se aplica a nivel de base de datos
          mediante políticas de Row-Level Security en Postgres.
        </p>
      </DocCard>

      <DocCard title="Roles dentro de una organización">
        <table className="docs-table">
          <thead><tr><th>Rol</th><th>Puede hacer</th></tr></thead>
          <tbody>
            <tr>
              <td><code className="cfg-code">admin</code></td>
              <td>Gestionar miembros, servicios, API keys y configuración de la org.</td>
            </tr>
            <tr>
              <td><code className="cfg-code">operator</code></td>
              <td>Crear y modificar servicios, incidentes y API keys. No gestiona miembros.</td>
            </tr>
            <tr>
              <td><code className="cfg-code">viewer</code></td>
              <td>Solo lectura: ver traces, servicios, incidentes.</td>
            </tr>
          </tbody>
        </table>
        <p>
          Un usuario puede pertenecer a varias organizaciones con roles
          distintos en cada una. Por ejemplo: <em>admin</em> en su org principal
          y <em>viewer</em> en una org colaboradora.
        </p>
      </DocCard>

      <DocCard title="Platform admin (cuenta de plataforma)">
        <p>
          El <strong>platform admin</strong> es un rol especial: ve todas las
          organizaciones, crea organizaciones nuevas y asigna usuarios. Se
          identifica por tener <code className="cfg-code">org_id = NULL</code> y
          el rol <code className="cfg-code">admin</code> a nivel global. No es
          miembro de ninguna org en particular.
        </p>
        <Callout kind="info">
          Si sos platform admin, vas a ver una sección{" "}
          <strong>Plataforma</strong> en el sidebar con opciones para gestionar
          Organizaciones y Usuarios.
        </Callout>
      </DocCard>

      <DocCard title="Cómo crear una organización (UI)">
        <ol className="docs-numbered">
          <li>
            Ingresá a <DocLink href="/organizations">Plataforma → Organizaciones</DocLink> (visible solo para platform admin).
          </li>
          <li>Presioná <strong>Nueva organización</strong>.</li>
          <li>
            Completá <strong>Nombre</strong> (legible, por ejemplo "Nuevo León") y{" "}
            <strong>Slug</strong> (kebab-case, por ejemplo "nuevo-leon"). El slug
            es el identificador estable que aparece en URLs.
          </li>
          <li>Presioná <strong>Crear</strong>.</li>
        </ol>
      </DocCard>

      <DocCard title="Cómo asignar usuarios a una organización">
        <ol className="docs-numbered">
          <li>Desde la lista de organizaciones, hacé click en una org para abrir su detalle.</li>
          <li>En la pestaña <strong>Miembros</strong>, presioná <strong>Agregar miembro</strong>.</li>
          <li>
            Elegí el modo:
            <ul className="docs-list" style={{ marginTop: 6 }}>
              <li>
                <strong>Crear usuario nuevo</strong>: completá email, nombre,
                contraseña inicial y rol. El usuario queda asignado a esta org.
              </li>
              <li>
                <strong>Asignar existente</strong>: pegá el UUID del usuario y
                elegí el rol que va a tener en esta org.
              </li>
            </ul>
          </li>
          <li>Presioná <strong>Agregar</strong>.</li>
        </ol>
      </DocCard>

      <DocCard title="Cambiar de organización activa">
        <p>
          En el topbar hay un selector que indica qué organización tenés activa.
          Si sos platform admin, aparece <em>Todas las organizaciones</em> +
          la lista; si pertenecés a varias, aparece un dropdown con todas
          tus orgs; si pertenecés a una sola, aparece como un badge fijo.
        </p>
        <p>
          La org seleccionada se manda en el header{" "}
          <code className="cfg-code">X-Org-Id</code> de cada request. Los
          servicios, traces e incidentes que ves se filtran automáticamente.
        </p>
        <Callout kind="info">
          En modo <em>"Todas las organizaciones"</em>, las tablas de traces y
          servicios incluyen una columna extra con la org de cada fila, para que
          puedas distinguir entre tenants de un vistazo.
        </Callout>
      </DocCard>

      <DocCard title="Aislamiento a nivel de base de datos">
        <p>
          Cada tabla con datos por tenant tiene activado{" "}
          <strong>Row-Level Security</strong> con una policy que filtra por{" "}
          <code className="cfg-code">org_id</code>. Cada request del backend
          setea <code className="cfg-code">app.current_org_id</code> en la
          sesión de Postgres, y todas las queries automáticamente devuelven
          solo las filas de esa org. Los platform admin pueden activar un flag
          de bypass que ignora la policy para tareas de administración.
        </p>
      </DocCard>
    </>
  );
}

function KeySection() {
  return (
    <>
      <DocCard title="Generar una API key desde la interfaz">
        <ol className="docs-numbered">
          <li>
            Ingresá a <DocLink href="/me">Configuración → API Keys</DocLink>.
          </li>
          <li>
            Presioná el botón <strong>Nueva</strong> en la esquina superior
            derecha.
          </li>
          <li>
            Asigná un nombre descriptivo que te permita identificarla más
            tarde (por ejemplo: <em>"backend producción"</em>, <em>"pipeline CI"</em>,
            <em> "laptop daniel"</em>).
          </li>
          <li>
            Copiá el token <code className="cfg-code">sk_...</code> que aparece
            en el cuadro amarillo. <strong>Se muestra una única vez</strong>; si
            no lo guardás en ese momento, deberás revocarla y generar otra.
          </li>
        </ol>
      </DocCard>

      <DocCard title="Guardar la API key en tu aplicación">
        <p>
          La práctica recomendada es almacenarla como variable de entorno y
          jamás incluirla en el repositorio.
        </p>
        <CodeBlock language="bash">{`SOVRA_API_KEY=sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</CodeBlock>
      </DocCard>

      <DocCard title="Características de la API key">
        <ul className="docs-list">
          <li>
            Hereda los roles y el <code className="cfg-code">org_id</code> del
            usuario que la generó.
          </li>
          <li>
            Es de larga duración: no expira automáticamente, pero puede
            revocarse en cualquier momento desde la misma pantalla.
          </li>
          <li>
            Una vez revocada, todas las integraciones que la utilicen recibirán
            <code className="cfg-code"> 401 Unauthorized</code> de forma inmediata.
          </li>
          <li>
            Si la key es comprometida, la acción recomendada es revocarla y
            generar una nueva.
          </li>
        </ul>
      </DocCard>

      <Callout kind="warning">
        Si extraviaste el token, no es posible recuperarlo. La única vía es
        revocar la key existente y emitir una nueva.
      </Callout>
    </>
  );
}

function ServiceSection() {
  return (
    <>
      <DocCard title="¿Qué es un servicio en Sovra Log?">
        <p>
          Un <strong>servicio</strong> representa un sistema externo o
          dependencia que tu aplicación consume y querés observar. Cada evento
          que envíes a Sovra Log debe referenciar un <code className="cfg-code">service_id</code>{" "}
          previamente registrado.
        </p>
        <p>
          Ejemplos típicos: una pasarela de pagos, un proveedor de identidad,
          una API gubernamental, un microservicio interno crítico.
        </p>
      </DocCard>

      <DocCard title="Crear un servicio desde la interfaz">
        <ol className="docs-numbered">
          <li>
            Ingresá a <DocLink href="/services">Servicios</DocLink> en el
            menú lateral.
          </li>
          <li>
            Presioná el botón <strong>Nuevo servicio</strong>.
          </li>
          <li>
            Completá los campos del formulario:
            <table className="docs-table" style={{ marginTop: 8 }}>
              <thead>
                <tr><th>Campo</th><th>Descripción</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>ID</strong></td>
                  <td>
                    Identificador único usado en cada evento (slug en minúsculas,
                    sin espacios). Ejemplo: <code className="cfg-code">pokeapi</code>.
                  </td>
                </tr>
                <tr>
                  <td><strong>Nombre</strong></td>
                  <td>
                    Nombre legible que aparece en el dashboard. Ejemplo:{" "}
                    <em>PokeAPI</em>.
                  </td>
                </tr>
                <tr>
                  <td><strong>Categoría</strong></td>
                  <td>
                    Agrupador opcional (por ejemplo: <em>external</em>, <em>identity</em>,
                    <em> payments</em>). Facilita filtrar el listado.
                  </td>
                </tr>
                <tr>
                  <td><strong>URL</strong></td>
                  <td>
                    URL base del sistema externo. Opcional; se utiliza como
                    referencia rápida desde el detalle del servicio.
                  </td>
                </tr>
              </tbody>
            </table>
          </li>
          <li>
            Presioná <strong>Crear</strong> para confirmar.
          </li>
        </ol>
      </DocCard>

      <Callout kind="info">
        El <code className="cfg-code">service_id</code> debe existir{" "}
        <strong>antes</strong> de que tu aplicación envíe eventos que lo
        referencien. Los eventos asociados a servicios inexistentes son
        rechazados durante el procesamiento.
      </Callout>
    </>
  );
}

function EmitSection({ apiBase }: { apiBase: ApiBaseInfo }) {
  return (
    <>
      <DocCard title="Patrón general">
        <p>
          Por cada llamada HTTP a un sistema externo, tu aplicación debe emitir
          dos eventos al endpoint de ingesta, ambos con el mismo{" "}
          <code className="cfg-code">correlation_id</code>:
        </p>
        <ol className="docs-numbered">
          <li>
            <strong>Antes</strong> de invocar al servicio →{" "}
            <code className="cfg-code">action: "request"</code>,{" "}
            <code className="cfg-code">sequence: 1</code>.
          </li>
          <li>
            <strong>Después</strong> de recibir la respuesta →{" "}
            <code className="cfg-code">action: "response"</code> si fue exitosa,
            o <code className="cfg-code">"error"</code> si falló.{" "}
            <code className="cfg-code">sequence: 2</code>.
          </li>
        </ol>
      </DocCard>

      <DocCard title="Endpoint">
        <CodeBlock language="bash">{`POST ${apiBase.endpointOrPath("/v1/ingest/events")}

Headers:
  X-Api-Key:    sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
  Content-Type: application/json

Body:
  { "events": [ /* uno o más eventos */ ] }`}</CodeBlock>
        <p>
          La respuesta es <code className="cfg-code">202 Accepted</code> con el
          conteo de eventos aceptados y rechazados.
        </p>
      </DocCard>

      <DocCard title="Estructura de un evento">
        <CodeBlock language="json">{`{
  "correlation_id": "uuid-v4",        // identificador del trace
  "service_id":     "pokeapi",        // service_id registrado en /services
  "sequence":       1,                // 1 = request, 2 = response
  "timestamp":      "2026-06-03T15:00:00Z",
  "action":         "request",        // "request" | "response" | "error"
  "level":          "info",           // "info" | "warn" | "error"
  "duration_ms":    142,              // duración en milisegundos (en el response)
  "tags": {                           // etiquetas de negocio, indexadas
    "operation": "get_pokemon",
    "pokemon":   "pikachu"
  },
  "request":        { "method": "GET", "path": "/api/v2/pokemon/pikachu" },
  "response":       { "status": 200, "body_size_bytes": 1024 },
  "error":          { "stack": "..." }   // solo cuando action = "error"
}`}</CodeBlock>
      </DocCard>

      <DocCard title="Ejemplo completo en Node.js">
        <p>
          Wrapper sobre <code className="cfg-code">fetch</code> que emite los
          dos eventos automáticamente y retorna la respuesta original.
        </p>
        <CodeBlock language="js">{`import { randomUUID } from "node:crypto";

const SOVRA_URL = process.env.SOVRA_URL;     // ej: ${apiBase.publicUrl ?? "http://localhost:4100"}
const API_KEY   = process.env.SOVRA_API_KEY; // sk_...

async function emit(events) {
  await fetch(\`\${SOVRA_URL}/v1/ingest/events\`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key":    API_KEY,
    },
    body: JSON.stringify({ events }),
  });
}

export async function callExternal(url, init, ctx) {
  const correlation_id = randomUUID();
  const startedAt      = Date.now();

  await emit([{
    correlation_id,
    service_id: ctx.service_id,
    sequence:   1,
    timestamp:  new Date().toISOString(),
    action:     "request",
    level:      "info",
    tags:       ctx.tags,
    request:    { method: init?.method ?? "GET", path: new URL(url).pathname },
  }]);

  try {
    const res = await fetch(url, init);
    await emit([{
      correlation_id,
      service_id: ctx.service_id,
      sequence:   2,
      timestamp:  new Date().toISOString(),
      action:     res.ok ? "response" : "error",
      level:      res.ok ? "info" : "error",
      duration_ms: Date.now() - startedAt,
      tags:       ctx.tags,
      response:   { status: res.status },
    }]);
    return { res, correlation_id };
  } catch (err) {
    await emit([{
      correlation_id,
      service_id: ctx.service_id,
      sequence:   2,
      timestamp:  new Date().toISOString(),
      action:     "error",
      level:      "error",
      duration_ms: Date.now() - startedAt,
      tags:       ctx.tags,
      error:      { stack: err.stack },
    }]);
    throw err;
  }
}

// Uso:
const { res, correlation_id } = await callExternal(
  "https://pokeapi.co/api/v2/pokemon/pikachu",
  { method: "GET" },
  { service_id: "pokeapi", tags: { pokemon: "pikachu" } },
);`}</CodeBlock>
      </DocCard>

      <Callout kind="info">
        El envío de eventos es <strong>idempotente</strong> por la combinación{" "}
        <code className="cfg-code">(correlation_id, sequence)</code>. Reenviar
        un mismo evento no genera duplicados, lo que permite implementar
        reintentos con seguridad.
      </Callout>
    </>
  );
}

function SearchSection({ apiBase }: { apiBase: ApiBaseInfo }) {
  return (
    <>
      <DocCard title="Búsqueda desde la interfaz">
        <p>
          Desde <DocLink href="/traces">Traces</DocLink> podés consultar todos
          los registros de tu organización. La barra superior ofrece filtros
          por servicio, estado y ventana de tiempo. Al seleccionar un trace,
          se despliega la línea temporal con los eventos en orden cronológico.
        </p>
      </DocCard>

      <DocCard title="Búsqueda vía API">
        <p>
          Los endpoints de lectura aceptan la misma API key que el endpoint
          de ingesta.
        </p>
        <CodeBlock language="bash">{`# Listar los traces más recientes
curl ${apiBase.endpointOrPath("/v1/traces")} \\
  -H "X-Api-Key: $SOVRA_API_KEY"

# Filtrar por tag (por ejemplo: pokemon = pikachu)
curl "${apiBase.endpointOrPath("/v1/traces")}?tags.pokemon=pikachu" \\
  -H "X-Api-Key: $SOVRA_API_KEY"

# Recuperar un trace específico por su correlation_id
curl ${apiBase.endpointOrPath("/v1/traces")}/<correlation_id> \\
  -H "X-Api-Key: $SOVRA_API_KEY"`}</CodeBlock>
      </DocCard>

      <DocCard title="Filtros disponibles">
        <table className="docs-table">
          <thead>
            <tr><th>Parámetro</th><th>Descripción</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><code className="cfg-code">service_ids</code></td>
              <td>Lista separada por coma de <code className="cfg-code">service_id</code>.</td>
            </tr>
            <tr>
              <td><code className="cfg-code">state</code></td>
              <td><code className="cfg-code">ok</code> · <code className="cfg-code">error</code> · <code className="cfg-code">pending</code>.</td>
            </tr>
            <tr>
              <td><code className="cfg-code">from</code> / <code className="cfg-code">to</code></td>
              <td>Ventana temporal en formato ISO 8601.</td>
            </tr>
            <tr>
              <td><code className="cfg-code">tags.&lt;clave&gt;</code></td>
              <td>Match exacto contra un tag de negocio. Ejemplo: <code className="cfg-code">tags.module=tramites</code>.</td>
            </tr>
            <tr>
              <td><code className="cfg-code">limit</code></td>
              <td>Cantidad máxima de resultados. Por defecto 50, máximo 200.</td>
            </tr>
          </tbody>
        </table>
      </DocCard>
    </>
  );
}

function DiagnoseSection({ apiBase }: { apiBase: ApiBaseInfo }) {
  return (
    <>
      <DocCard title="Propósito">
        <p>
          El endpoint <code className="cfg-code">GET /v1/traces/diagnose</code>{" "}
          fue diseñado para integraciones automatizadas — típicamente, un
          agente de IA que clasifica tickets de soporte. A partir del contexto
          extraído de un ticket (folio, módulo, ventana de tiempo, etc.),
          devuelve los traces que coinciden y, para cada uno, un{" "}
          <strong>veredicto</strong> calculado por el servidor que indica dónde
          ocurrió el problema.
        </p>
        <p>
          A diferencia del listado de traces, este endpoint resume el resultado
          de cada interacción en una sola etiqueta accionable, lista para ser
          consumida por un agente sin lógica adicional.
        </p>
      </DocCard>

      <DocCard title="Flujo de uso típico">
        <pre className="docs-diagram">{`Ticket en herramienta de soporte
   │   (captura de pantalla con folio, módulo y hora del error)
   ▼
Agente extrae entidades  ──▶  { folio, module, time, ref? }
   │
   ▼
GET /v1/traces/diagnose?folio=...&module=...&from=...&to=...
   │
   ▼
Respuesta: { matches: [{ verdict, verdict_reason, ... }] }
   │
   ▼
Agente publica resumen en el ticket o lo redirige al equipo correcto`}</pre>
      </DocCard>

      <DocCard title="Endpoint y autenticación">
        <CodeBlock language="bash">{`GET ${apiBase.endpointOrPath("/v1/traces/diagnose")}

Headers:
  X-Api-Key: sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</CodeBlock>
      </DocCard>

      <DocCard title="Parámetros de consulta">
        <p>
          Todos los parámetros son opcionales. Cuantos más se proporcionen, más
          preciso será el match. Si se incluye <code className="cfg-code">correlation_id</code>,
          tiene prioridad sobre el resto.
        </p>
        <table className="docs-table">
          <thead>
            <tr><th>Parámetro</th><th>Mapea a</th><th>Ejemplo</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><code className="cfg-code">correlation_id</code></td>
              <td>Match exacto. Prevalece sobre cualquier otro filtro.</td>
              <td><code className="cfg-code">?correlation_id=abc-123</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">folio</code></td>
              <td><code className="cfg-code">tags @&gt; {`{"folio": …}`}</code></td>
              <td><code className="cfg-code">?folio=ICV-9</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">module</code></td>
              <td><code className="cfg-code">tags @&gt; {`{"module": …}`}</code></td>
              <td><code className="cfg-code">?module=tramites</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">operation</code></td>
              <td><code className="cfg-code">tags @&gt; {`{"operation": …}`}</code></td>
              <td><code className="cfg-code">?operation=validar_identidad</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">service</code></td>
              <td><code className="cfg-code">service_id</code> presente en el trace</td>
              <td><code className="cfg-code">?service=pokeapi</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">outcome</code></td>
              <td><code className="cfg-code">"error"</code> o <code className="cfg-code">"success"</code>, mapea a <code className="cfg-code">state</code></td>
              <td><code className="cfg-code">?outcome=error</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">from</code> / <code className="cfg-code">to</code></td>
              <td>Ventana de tiempo en ISO 8601</td>
              <td><code className="cfg-code">?from=2026-06-03T13:00:00Z&to=2026-06-03T15:00:00Z</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">limit</code></td>
              <td>Cantidad máxima de matches. Por defecto 20, máximo 100.</td>
              <td><code className="cfg-code">?limit=5</code></td>
            </tr>
          </tbody>
        </table>
      </DocCard>

      <DocCard title="Estructura de la respuesta">
        <p>
          La respuesta contiene un arreglo <code className="cfg-code">matches</code>{" "}
          y el total encontrado. Cada match incluye los campos del trace junto
          con los campos derivados que facilitan la clasificación.
        </p>
        <CodeBlock language="json">{`{
  "total":   1,
  "matches": [{
    "correlation_id":   "abc-123",
    "verdict":          "external_fault",
    "verdict_reason":   "El sistema externo devolvió 503",
    "error_origin":     "external",
    "external_reached": true,
    "last_status":      503,
    "state":            "error",
    "started_at":       "2026-06-03T14:32:08Z",
    "ended_at":         "2026-06-03T14:32:16Z",
    "duration_ms":      8200,
    "tags":             { "folio": "ICV-9", "module": "tramites" },
    "service_ids":      ["identidad.renaper"],
    "end_user_email":   "ciudadano@ejemplo.com",
    "end_user_id_hash": "sha256(...)",
    "event_count":      2,
    "events": [
      { "sequence": 1, "action": "request",  "service_id": "identidad.renaper" },
      { "sequence": 2, "action": "error",    "response": { "status": 503 } }
    ]
  }]
}`}</CodeBlock>
      </DocCard>

      <DocCard title="Posibles valores del veredicto">
        <table className="docs-table">
          <thead>
            <tr><th>Veredicto</th><th>Significado</th><th>error_origin</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><code className="cfg-code">ok</code></td>
              <td>El trace finalizó correctamente. No hay fallo asociado.</td>
              <td>—</td>
            </tr>
            <tr>
              <td><code className="cfg-code">external_fault</code></td>
              <td>El sistema externo devolvió un código <code className="cfg-code">5xx</code>. La responsabilidad es del proveedor.</td>
              <td><code className="cfg-code">external</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">internal_fault</code></td>
              <td>El externo respondió <code className="cfg-code">4xx</code>. La solicitud enviada por tu aplicación tenía un problema.</td>
              <td><code className="cfg-code">internal</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">network</code></td>
              <td>Falla de conectividad: timeout, DNS, conexión rechazada. No se llegó a alcanzar al externo.</td>
              <td><code className="cfg-code">network</code></td>
            </tr>
            <tr>
              <td><code className="cfg-code">unknown</code></td>
              <td>Hay error registrado pero la información no permite clasificarlo automáticamente.</td>
              <td><code className="cfg-code">unknown</code></td>
            </tr>
          </tbody>
        </table>
        <Callout kind="info">
          El veredicto no se persiste en la base de datos: se calcula en cada
          consulta a partir de los eventos del trace. Si las reglas de
          clasificación mejoran, los traces históricos se ven beneficiados
          sin necesidad de reprocesamiento.
        </Callout>
      </DocCard>

      <DocCard title="Ejemplo de uso desde un agente">
        <p>
          A partir de las entidades extraídas del ticket, el agente construye
          la consulta y publica un mensaje basado en el veredicto.
        </p>
        <CodeBlock language="js">{`async function clasificarTicket(ticket) {
  // 1. Extraer entidades del ticket (folio, módulo, ventana de tiempo)
  const { folio, module, time, ref } = await extraerEntidades(ticket);

  // 2. Consultar a Sovra Log
  const params = new URLSearchParams({
    correlation_id: ref ?? "",
    folio:          folio ?? "",
    module:         module ?? "",
    from:           new Date(time - 5 * 60_000).toISOString(),
    to:             new Date(time + 5 * 60_000).toISOString(),
    outcome:        "error",
    limit:          "5",
  });

  const res = await fetch(
    \`\${SOVRA_URL}/v1/traces/diagnose?\${params}\`,
    { headers: { "X-Api-Key": process.env.SOVRA_API_KEY } },
  );
  const { matches, total } = await res.json();

  // 3. Construir respuesta según el veredicto
  if (total === 0) {
    return "No se encontraron llamadas que coincidan con el contexto del ticket.";
  }

  const m = matches[0];
  switch (m.verdict) {
    case "external_fault":
      return \`Falla del proveedor: \${m.service_ids[0]} devolvió \${m.last_status}. Trace: \${m.correlation_id}\`;
    case "internal_fault":
      return \`La solicitud enviada al externo era inválida (\${m.last_status}). Trace: \${m.correlation_id}\`;
    case "network":
      return \`Problema de conectividad con el externo: \${m.verdict_reason}\`;
    case "ok":
      return "La interacción finalizó correctamente; el ticket puede deberse a otra causa.";
    default:
      return \`Se encontró el trace \${m.correlation_id} pero no es posible clasificarlo automáticamente.\`;
  }
}`}</CodeBlock>
      </DocCard>

      <Callout kind="info">
        <strong>Recomendación de integración:</strong> si tu aplicación
        retorna el <code className="cfg-code">correlation_id</code> al usuario
        final cuando ocurre un error (por ejemplo en un mensaje del tipo{" "}
        <em>"Referencia: abc-123"</em>), el agente puede usarlo como filtro
        principal, obteniendo un match exacto sin depender de OCR ni de la
        ventana temporal.
      </Callout>
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface ApiBaseInfo {
  publicUrl: string | null;
  endpointOrPath(path: string): string;
}

function useApiBase(): ApiBaseInfo {
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  return useMemo<ApiBaseInfo>(() => {
    const raw = (import.meta.env.VITE_API_BASE as string | undefined)?.trim() ?? "";
    if (!raw) return { publicUrl: null, endpointOrPath: (p) => p };
    if (/^https?:\/\/(localhost|127\.0\.0\.1)/i.test(raw))
      return { publicUrl: null, endpointOrPath: (p) => p };
    const trimmed = raw.replace(/\/$/, "");
    return { publicUrl: trimmed, endpointOrPath: (p) => `${trimmed}${p}` };
  }, []);
}

function DocCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="settings-card">
      <header><div><h3>{title}</h3></div></header>
      <div className="settings-card-body docs-prose">{children}</div>
    </section>
  );
}

function Callout({ kind, children }: { kind: "info" | "warning"; children: ReactNode }) {
  const isWarning = kind === "warning";
  const palette = isWarning
    ? { bg: "color-mix(in oklab, var(--st-degraded) 10%, transparent)", border: "color-mix(in oklab, var(--st-degraded) 30%, transparent)", color: "var(--st-degraded)" }
    : { bg: "color-mix(in oklab, var(--accent) 8%, transparent)", border: "color-mix(in oklab, var(--accent) 25%, transparent)", color: "var(--fg-1)" };

  const I = isWarning ? Icons.AlertTriangle : Icons.Sparkles;
  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.color,
        padding: 12,
        borderRadius: 8,
        margin: "12px 0",
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 10,
        alignItems: "start",
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      <I size={14} sw={1.8} />
      <div>{children}</div>
    </div>
  );
}

function CodeBlock({ children, language }: { children: string; language?: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="cfg-code-block" style={{ position: "relative", margin: "12px 0" }}>
      <button
        type="button"
        onClick={copy}
        className="cfg-code-copy"
        title="Copiar al portapapeles"
        style={{ display: "flex", alignItems: "center", gap: 4 }}
      >
        <Icons.Copy size={11} />
        <span>{copied ? "Copiado" : "Copiar"}</span>
      </button>
      <pre data-language={language ?? ""} style={{ paddingTop: 36 }}>{children}</pre>
    </div>
  );
}

function DocLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 500 }}>
      {children}
    </a>
  );
}
